/**
 * WISHLIST CONTACT MODAL
 *
 * Modal for contacting a user about an item on their wishlist.
 * Used when viewing someone else's public wishlist and you have
 * an item they're looking for.
 *
 * Priority colors are centralized in src/constants/categoryColors.ts
 * See PriorityColors for current colors (ISO=sky, DISO=amber, Unicorn=purple)
 *
 * Usage:
 * <WishlistContactModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   currentUserId={myUserId}
 *   wisherId={profileOwnerId}
 *   wisherUsername={profile.username}
 *   item={wishlistItem}
 * />
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendInquiry, getUserInfo } from '../messaging/messagingService';
import type { AttachedItem } from '../messaging/types';
import { X, Send, Loader2, Gift } from 'lucide-react';
import { PriorityColors, type PriorityKey } from '../constants/categoryColors';

interface WishlistItem {
  id: string;
  name: string;
  brand: string;
  wishlistPriority?: 'iso' | 'diso' | 'unicorn';
  isoPriority?: 'iso' | 'diso' | 'unicorn'; // Legacy support
  photos?: { url: string; isDefault?: boolean }[];
  photoUrl?: string;
}

interface WishlistContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId: string;
  wisherId: string;
  wisherUsername: string | null;
  wisherDisplayName?: string;
  item: WishlistItem;
}

const WishlistContactModal: React.FC<WishlistContactModalProps> = ({
  isOpen,
  onClose,
  currentUserId,
  wisherId,
  wisherUsername,
  wisherDisplayName,
  item
}) => {
  const navigate = useNavigate();
  
  // Support both wishlistPriority and legacy isoPriority
  const priority = item.wishlistPriority || item.isoPriority || 'iso';
  
  // Get priority label for the message using centralized PriorityColors
  const getPriorityLabel = () => {
    const key = (priority || 'iso') as PriorityKey;
    return PriorityColors[key]?.label || PriorityColors.iso.label;
  };
  
  // Default message
  const defaultMessage = `Hi! I saw ${item.brand} ${item.name} is on your ${getPriorityLabel()} list. I have one and wanted to reach out in case you're interested!`;
  
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  // Get item photo
  const getItemPhoto = (): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    if (item.photoUrl) return item.photoUrl;
    return null;
  };

  // Get priority badge component - using centralized PriorityColors
  const getPriorityBadge = () => {
    const key = (priority || 'iso') as PriorityKey;
    const colors = PriorityColors[key] || PriorityColors.iso;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${colors.bg} text-white text-xs font-bold rounded-full`}>
        {colors.label}
      </span>
    );
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;

    setSending(true);

    try {
      // Get user info for both participants
      const currentUserInfo = await getUserInfo(currentUserId);
      const wisherInfo = await getUserInfo(wisherId);

      // Create attached item (the wishlist item we're contacting about)
      // Note: This item belongs to the WISHER, not the sender
      const attachedItem: AttachedItem = {
        itemId: item.id,
        ownerId: wisherId, // The wisher owns this wishlist item
        name: item.name,
        brand: item.brand,
        photoUrl: getItemPhoto(),
        status: 'available', // It's on their wishlist, so they don't have it yet
      };

      // Send the inquiry - this creates/opens a conversation
      const conversationId = await sendInquiry(
        currentUserId,
        wisherId,
        currentUserInfo,
        wisherInfo,
        [attachedItem],
        message.trim()
      );

      // Navigate to the conversation
      onClose();
      navigate(`/messages/${conversationId}`);
    } catch (err) {
      console.error('Error sending wishlist contact:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border border-gray-300 bg-gradient-to-r from-fuchsia-500 to-purple-600">
          <div className="flex items-center gap-2 text-white">
            <Gift className="icon-md" />
            <h2 className="font-bold text-lg font-body">
              I Have This!
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition-default text-white"
          >
            <X className="icon-md" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Recipient */}
          <div className="mb-4">
            <p className="text-sm text-light-muted mb-1 font-body">To:</p>
            <p className="font-bold text-light-primary font-body">
              {wisherUsername ? `@${wisherUsername}` : wisherDisplayName || 'User'}
            </p>
          </div>

          {/* Item they're looking for */}
          <div className="mb-4 p-3 border border-gray-200 rounded-lg">
            <p className="text-sm text-light-muted mb-2 font-body">
              They're looking for:
            </p>
            <div className="flex items-center gap-3">
              {/* Item thumbnail */}
              {getItemPhoto() ? (
                <img 
                  src={getItemPhoto()!} 
                  alt={item.name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 rainbow-gradient rounded-lg flex items-center justify-center text-2xl">
                  💅
                </div>
              )}
              
              {/* Item info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-light-primary font-bold font-heading">
                  {item.brand}
                </p>
                <p className="font-bold text-light-primary truncate font-body">
                  {item.name}
                </p>
                <div className="mt-1">
                  {getPriorityBadge()}
                </div>
              </div>
            </div>
          </div>

          {/* Message input */}
          <div className="mb-4">
            <p className="text-sm text-light-muted mb-2 font-body">Your message:</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border border-gray-300 resize-none font-body"
              disabled={sending}
              autoFocus
            />
          </div>
          
          {/* Helpful tip */}
          <p className="text-xs text-light-muted text-center mb-4 font-body">
            💡 Include details like condition, price, and shipping availability
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border border-gray-300 border border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-light-secondary hover:bg-gray-200 rounded-lg transition-default font-body"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!message.trim() || sending}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-default font-body ${
              message.trim() && !sending
                ? 'btn-primary'
                : 'bg-gray-300 text-light-muted cursor-not-allowed'
            }`}
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="icon-sm" />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WishlistContactModal;
